/**
 * Page Context Detector
 * Detects current page section and provides contextual information for chatbot
 */

export interface IPageContext {
  path: string;
  title: string;
  section: string;
  params?: Record<string, string>;
}

/**
 * Get current section from pathname
 */
export const getCurrentSection = (pathname: string): string => {
  // Remove leading/trailing slashes and split
  const segments = pathname.replace(/^\/|\/$/g, '').split('/');
  const firstSegment = segments[0] || 'home';

  // Map routes to sections
  // Only include routes that have corresponding quick replies with function support
  const sectionMap: Record<string, string> = {
    portal: 'knowledge_base', // Has search_hr_policy function
    qa: 'knowledge_base', // QA page also uses ask-service
    '': 'home' // Default home page
  };

  // Return mapped section or default to 'home' for unknown routes
  return sectionMap[firstSegment] || 'home';
};

/**
 * Extract URL parameters
 */
export const extractParams = (pathname: string): Record<string, string> => {
  const params: Record<string, string> = {};
  const segments = pathname.split('/').filter(Boolean);

  // Extract ID-like params
  segments.forEach((segment, index) => {
    if (/^\d+$/.test(segment) && index > 0) {
      const paramName = segments[index - 1];
      params[`${paramName}_id`] = segment;
    }
  });

  return params;
};

/**
 * Get full page context
 */
export const getPageContext = (): IPageContext => {
  if (typeof window === 'undefined') {
    return {
      path: '/',
      title: '',
      section: 'home',
      params: {}
    };
  }

  const pathname = window.location.pathname;
  const title = document.title;
  const section = getCurrentSection(pathname);
  const params = extractParams(pathname);

  return {
    path: pathname,
    title,
    section,
    params
  };
};
